if (!customElements.get('theme-sticky-header')) {
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.header = this.querySelector('.header');
      this.announcementBar = this.querySelector('.announcement-bar');
      this.mode = this.header?.dataset.stickyMode || 'none';

      this._abortController = new AbortController();
      const signal = this._abortController.signal;

      this._publishHeights();
      this._resizeObserver = new ResizeObserver(() => {
        this._publishHeights();
        this._publishFloatingBottom();
      });
      if (this.header) this._resizeObserver.observe(this.header);
      if (this.announcementBar) this._resizeObserver.observe(this.announcementBar);

      if (!this.header || this.mode === 'none') return;

      this.addEventListener('transitionend', (event) => {
        if (event.propertyName === 'transform') this._publishFloatingBottom();
      }, { signal });

      this._rafId = null;
      this.lastScrollY = window.scrollY;
      this.scrolledThreshold = 0;
      this._directionAnchor = window.scrollY;
      this._currentDirection = null;

      const onScroll = this.mode === 'hide_on_scroll_down'
        ? this._handleHideOnScrollDown.bind(this)
        : this._handleAlways.bind(this);

      window.addEventListener('scroll', () => {
        if (this._rafId) return;
        this._rafId = requestAnimationFrame(() => {
          onScroll();
          this._rafId = null;
        });
      }, { passive: true, signal });
    }

    disconnectedCallback() {
      this._abortController?.abort();
      this._resizeObserver?.disconnect();
      if (this._rafId) cancelAnimationFrame(this._rafId);
    }

    _publishHeights() {
      if (this.header) {
        document.documentElement.style.setProperty('--header-height', `${this.header.offsetHeight}px`);
      }
      if (this.announcementBar) {
        document.documentElement.style.setProperty('--announcement-bar-height', `${this.announcementBar.offsetHeight}px`);
      }
    }

    _handleAlways() {
      const currentScrollY = window.scrollY;
      this.header.classList.toggle('header--scrolled', currentScrollY > this.scrolledThreshold);
      this.classList.toggle('sticky-group--bar-hidden', currentScrollY > this.scrolledThreshold);
      this._publishFloatingBottom();
    }

    _handleHideOnScrollDown() {
      const currentScrollY = window.scrollY;
      const delta = currentScrollY - this.lastScrollY;
      this.lastScrollY = currentScrollY;

      this.header.classList.toggle('header--scrolled', currentScrollY > this.scrolledThreshold);

      if (currentScrollY <= this.scrolledThreshold) {
        this.classList.remove('sticky-group--bar-hidden', 'sticky-group--fully-hidden');
        this._currentDirection = null;
        this._publishFloatingBottom();
        return;
      }

      this.classList.add('sticky-group--bar-hidden');

      const rawDirection = delta > 0 ? 'down' : delta < 0 ? 'up' : null;
      if (!rawDirection) {
        this._publishFloatingBottom();
        return;
      }

      if (rawDirection !== this._currentDirection) {
        this._directionAnchor = currentScrollY;
        this._currentDirection = rawDirection;
      }

      const accumulated = Math.abs(currentScrollY - this._directionAnchor);
      if (accumulated < 10) {
        this._publishFloatingBottom();
        return;
      }

      const atBottom = currentScrollY + window.innerHeight >= document.documentElement.scrollHeight - 8;

      if (this._currentDirection === 'down' && currentScrollY > this.offsetHeight) {
        this.classList.add('sticky-group--fully-hidden');
      } else if (this._currentDirection === 'up' && !atBottom) {
        this.classList.remove('sticky-group--fully-hidden');
      }

      this._publishFloatingBottom();
    }

    _publishFloatingBottom() {
      if (!this.header || this.mode === 'none') {
        document.documentElement.style.removeProperty('--header-floating-bottom');
        return;
      }

      if (window.scrollY <= this.scrolledThreshold) {
        document.documentElement.style.removeProperty('--header-floating-bottom');
        return;
      }

      document.documentElement.style.setProperty(
        '--header-floating-bottom',
        `${Math.max(this.getBoundingClientRect().bottom, 0)}px`
      );
    }
  }

  customElements.define('theme-sticky-header', StickyHeader);
}
